import { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Row, Button, Col, ButtonGroup, Tabs, Tab, Spinner } from 'react-bootstrap';
// import Collapse  from 'react-bootstrap/Collapse';
import { User } from '../../models/interfaces/User';
import { Event, EventAdmin, isSameDay } from '../../models/interfaces/Event';
import { Competition, isCompetition, sortCompetitions } from '../../models/interfaces/Competition';
import { useUpdatableProviderToArray } from '../../helpers/useListProviderToArray';
import { SportProvider } from '../../models/interfaces/SportProvider';
import EventRegistrationModal from './registration/EventRegistrationModal';
import DetailPageContainer from '../../components/containers/DetailPageContainer';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faClock, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import EventCompetitionMatchView from './competitions/EventCompetitionMatchView';
import EventCompetitionView from './competitions/EventCompetitionView';
import { Match, MatchStatus } from '../../models/interfaces/Match';
import EventCompetitionStandingsView from './competitions/EventCompetitionStandingsView';
import EventCompetitionLeadersView from './competitions/EventCompetitionLeadersView';
import EventCompetitionPicker from './competitions/EventCompetitionPicker';
import { DivisionButtonContent } from './admin/EventCompetitionAdminTable';

interface Props {
    user?: User;
    event: Event;
    sportProvider: SportProvider;
}

interface CachedMatches {
    scheduledMatches: Match[];
    completedMatches: Match[];
}

export default function EventDetailPage({ user, event, sportProvider }: Props) {
    const history = useHistory();
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);

    const [eventAdmin, setEventAdmin] = useState<EventAdmin | undefined>(undefined);
    const [selectedCompetition, setSelectedCompetition] = useState<Competition | undefined>(
        undefined
    );

    const [competitionMatches, setCompetitionMatches] = useState(new Map<String, CachedMatches>());
    useMemo(() => {
        if (!!!selectedCompetition) {
            return;
        }
        const existingMatchesForCompetition = competitionMatches.get(selectedCompetition.id);
        if (existingMatchesForCompetition) {
            return;
        }
        selectedCompetition.matchProvider
            .once()
            .then((matchResolvers) => {
                const matchPromises = matchResolvers.map((matchResolver) => {
                    return matchResolver.asAPromise();
                });
                return Promise.all(matchPromises);
            })
            .then((matches) => {
                var updatedMatches = new Map(competitionMatches);
                let cachedMatches: CachedMatches = { scheduledMatches: [], completedMatches: [] };
                let groupedMatches = matches.reduce((currentCachedMatches, incomingMatch) => {
                    if (incomingMatch.status == MatchStatus.completed) {
                        var completedMatches = currentCachedMatches.completedMatches;
                        completedMatches.push(incomingMatch);
                        return {
                            scheduledMatches: currentCachedMatches.scheduledMatches,
                            completedMatches,
                        };
                    } else {
                        var scheduledMatches = currentCachedMatches.scheduledMatches;
                        scheduledMatches.push(incomingMatch);
                        return {
                            scheduledMatches,
                            completedMatches: currentCachedMatches.completedMatches,
                        };
                    }
                }, cachedMatches);
                let sortedMatches: CachedMatches = {
                    scheduledMatches: groupedMatches.scheduledMatches.sort((a, b) => {
                        let aDate = a.date;
                        let bDate = b.date;
                        if (aDate < bDate) {
                            return -1;
                        }
                        return 0;
                    }),
                    completedMatches: groupedMatches.completedMatches.sort((a, b) => {
                        let aDate = a.date;
                        let bDate = b.date;
                        if (aDate > bDate) {
                            return -1;
                        }
                        return 0;
                    }),
                };
                updatedMatches.set(selectedCompetition.id, sortedMatches);
                setCompetitionMatches(updatedMatches);
            });
    }, [selectedCompetition]);

    const updateEventAdmin = useCallback(
        (user) => {
            event
                .adminForUser(user)
                .then((eventAdmin) => {
                    setEventAdmin(eventAdmin);
                })
                .catch(() => {
                    setEventAdmin(undefined);
                });
        },
        [event, history]
    );

    useEffect(() => {
        if (user && !eventAdmin) {
            updateEventAdmin(user);
        }
        if (!user && eventAdmin) {
            setEventAdmin(undefined);
        }
    }, [event, user, updateEventAdmin, eventAdmin]);

    const { state: competitions, triggerUpdate: competitionsDidUpdate } =
        useUpdatableProviderToArray({
            provider: event.competitionProvider,
            isType: isCompetition,
            sortFn: sortCompetitions,
        });

    const handleHideModal = useCallback(() => {
        setShowRegistrationModal(false);
    }, []);

    const eventRegistration = event.registration;
    return (
        <DetailPageContainer
            titleView={
                <Col className="py-2">
                    <Row>
                        <h3>{event.attributes.name}</h3>
                    </Row>
                    <Row>
                        <EventSubtitleView event={event} />
                    </Row>
                </Col>
            }
            accessoryView={
                <ButtonGroup className="mb-1">
                    {eventRegistration && user && (
                        <>
                            <Button
                                onClick={() => {
                                    const registrationPrice = eventRegistration.attributes.price;
                                    if (registrationPrice && registrationPrice > 0) {
                                        history.push(`/eventRegistration/${event.id}`);
                                    } else {
                                        setShowRegistrationModal(true);
                                    }
                                }}
                            >
                                Register
                            </Button>
                            <EventRegistrationModal
                                user={user}
                                sportProvider={sportProvider}
                                registration={eventRegistration}
                                showModal={showRegistrationModal}
                                hideModal={handleHideModal}
                                onCompleteRegistrationRequest={() => {}}
                            />
                        </>
                    )}
                    {eventAdmin && (
                        <Button
                            variant="info"
                            onClick={() => {
                                history.push(`/events/${event.id}/admin`);
                            }}
                        >
                            Admin
                        </Button>
                    )}
                </ButtonGroup>
            }
            sportProvider={sportProvider}
        >
            {typeof competitions === 'undefined' ? (
                <Spinner as="span" animation="grow" role="status" aria-hidden="true" />
            ) : competitions.length == 0 ? (
                <NoCompetitionsView />
            ) : typeof selectedCompetition === 'undefined' ? (
                <EventCompetitionPicker
                    sportProvider={sportProvider}
                    competitions={competitions}
                    selectedCompetition={selectedCompetition}
                    onSelectCompetition={(incomingCompetition) => {
                        setSelectedCompetition(incomingCompetition);
                    }}
                />
            ) : (
                <Container className="pt-2 px-1 mx-1">
                    <Button
                        className="px-0"
                        onClick={() => {
                            setSelectedCompetition(undefined);
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '8px',
                                paddingLeft: '4px',
                                paddingRight: '4px',
                            }}
                        >
                            <FontAwesomeIcon icon={faChevronLeft as IconProp} className="fa-1x" />
                            <DivisionButtonContent>
                                <strong>{selectedCompetition.attributes.name}</strong>
                                <small>
                                    {sportProvider.displayTextForTeamOrCompetitionAttributes(
                                        selectedCompetition.attributes,
                                        true
                                    )}
                                </small>
                            </DivisionButtonContent>
                        </div>
                    </Button>
                    <hr />
                    <Tabs>
                        <Tab title="Results" eventKey="results">
                            <EventCompetitionMatchView
                                competition={selectedCompetition}
                                matches={
                                    competitionMatches.get(selectedCompetition.id)?.completedMatches
                                }
                                noMatchText={'No matches completed. Check back soon!'}
                            />
                        </Tab>
                        <Tab title="Schedule" eventKey="schedule">
                            <EventCompetitionMatchView
                                competition={selectedCompetition}
                                matches={
                                    competitionMatches.get(selectedCompetition.id)?.scheduledMatches
                                }
                                noMatchText={'No matches scheduled. Check back soon!'}
                            />
                        </Tab>
                        <Tab title="Standings" eventKey="standings">
                            <EventCompetitionStandingsView competition={selectedCompetition} />
                        </Tab>
                        <Tab title="Leaderboard" eventKey="leaders">
                            <EventCompetitionLeadersView competition={selectedCompetition} />
                        </Tab>
                    </Tabs>
                </Container>
            )}
        </DetailPageContainer>
    );
}

const AttributeContent = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const AttributeText = styled.span`
    min-width: 100px;
`;

const AttributeContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

function EventAttributeView({ icon, text }: { icon: IconProp; text: string }) {
    return (
        <AttributeContent>
            <FontAwesomeIcon className="mb-1" icon={icon} />
            <>
                <AttributeText className="text-muted mb-0">{text}</AttributeText>
            </>
        </AttributeContent>
    );
}

function EventSubtitleView({ event }: { event: Event }) {
    var dateString: string | undefined = undefined;

    if (event.attributes.displayCompetitionDates) {
        const startDate = event.attributes.competitionDateRange.startDate;
        const endDate = event.attributes.competitionDateRange.endDate;
        const singleDayEvent = isSameDay(startDate, endDate);

        if (singleDayEvent) {
            dateString = startDate.toLocaleDateString();
        } else {
            dateString = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
        }
    }
    return (
        <AttributeContainer>
            {event.attributes.category && (
                <EventAttributeView icon={faLayerGroup} text={event.attributes.category.name} />
            )}

            {dateString && <EventAttributeView icon={faClock} text={dateString} />}
        </AttributeContainer>
    );
}

function NoCompetitionsView() {
    return (
        <div className="text-center mt-1">
            <h4>No Divisions added.</h4>
            <h5>Check back soon!</h5>
        </div>
    );
}

function SelectCompetitionView({ text }: { text: string }) {
    return (
        <div className="text-center mt-1">
            <h4>{text}</h4>
        </div>
    );
}
